/**
 * Checkout Utility Functions
 *
 * Helper functions for building checkout URLs and handling redirects
 */

import type { RoomCombination } from '@/services/mappers/offerMapper';
import type { RoomOccupancy } from '@/types/SearchBar';
import { serializePaxString, serializeAnnotatedPaxString } from '@/utils/parsing/paxParser';
import { CHECKOUT_CONFIG } from '@/constants/checkout/config';
import { isAvailableCode } from '@/constants/davinci/returnCodes';

/**
 * Build checkout URL with all required booking parameters
 *
 * @param combination - The selected room combination (single or multi-room)
 * @param productCode - Product/hotel code
 * @param roomOccupancy - Room occupancy with passengers
 * @returns Complete checkout URL with query parameters
 *
 * @example Single-room
 * const url = buildCheckoutUrl(
 *   { products: [offer], totalPrice: 1000, ... },
 *   'HHNMAR225',
 *   { rooms: [{ persons: [{ type: 'adult' }, { type: 'child', age: 10 }] }] }
 * );
 * // Returns: /checkout?offerIds=123&productCode=HHNMAR225&roomCodes=J02&...
 *
 * @example Multi-room
 * const url = buildCheckoutUrl(
 *   { products: [offer1, offer2], totalPrice: 2388, ... },
 *   'HHNMAR225',
 *   { rooms: [{ persons: [...] }, { persons: [...] }] }
 * );
 * // Returns: /checkout?offerIds=15,1&productCode=HHNMAR225&roomCodes=D03,D01&...
 */
export function buildCheckoutUrl(
  combination: RoomCombination,
  productCode: string,
  roomOccupancy: RoomOccupancy
): string {
  const url = new URL(CHECKOUT_CONFIG.BASE_URL, window.location.origin);

  const offerIds = combination.products.map((p) => p.offerId).join('-');
  url.searchParams.set(CHECKOUT_CONFIG.QUERY_PARAMS.OFFER_IDS, offerIds);

  url.searchParams.set(CHECKOUT_CONFIG.QUERY_PARAMS.PRODUCT_CODE, productCode);

  const roomCodes = combination.products.map((p) => p.roomCode).join('-');
  url.searchParams.set(CHECKOUT_CONFIG.QUERY_PARAMS.ROOM_CODES, roomCodes);

  url.searchParams.set(CHECKOUT_CONFIG.QUERY_PARAMS.BOARD_CODE, combination.boardCode);

  const serviceVariantIds = combination.products.map((p) => p.serviceVariantId).join('-');
  url.searchParams.set(CHECKOUT_CONFIG.QUERY_PARAMS.SERVICE_VARIANT_IDS, serviceVariantIds);

  // Format dates as YYYY-MM-DD (readable ISO format without time)
  const checkInDate = combination.startDate.split('T')[0];
  const checkOutDate = combination.endDate.split('T')[0];
  url.searchParams.set(CHECKOUT_CONFIG.QUERY_PARAMS.CHECK_IN, checkInDate);
  url.searchParams.set(CHECKOUT_CONFIG.QUERY_PARAMS.CHECK_OUT, checkOutDate);

  // Serialize room occupancy to the ANNOTATED pax string (e.g. r35a-1i-4c-16a) so the
  // hotel's Adult/Child/Infant classification survives the redirect. Falls back to the
  // plain form when any passenger lacks a classification (binary mode), never a
  // partially-annotated string.
  const paxString = serializeAnnotatedPaxString(roomOccupancy) ?? serializePaxString(roomOccupancy);
  if (paxString) {
    url.searchParams.set(CHECKOUT_CONFIG.QUERY_PARAMS.PAX, paxString);
  }

  // Preserve debug parameter from current URL
  const currentParams = new URLSearchParams(window.location.search);
  const debugValue = currentParams.get('debug');
  if (debugValue) {
    url.searchParams.set('debug', debugValue);
  }

  return url.toString();
}

/**
 * Check if availability code should trigger checkout redirect
 *
 * Uses centralized isAvailableCode() from registry to determine if booking flow
 * should proceed to checkout page. Supports codes 712, 707, 733, 734, and 923.
 *
 * @param code - Availability check result code from DaVinci API
 * @returns True if code indicates availability (can proceed to checkout)
 *
 * @example
 * if (shouldRedirectToCheckout(712)) {
 *   window.location.href = buildCheckoutUrl(...);
 * }
 */
export function shouldRedirectToCheckout(code: number): boolean {
  return isAvailableCode(code);
}
